/**
 * MCC Quote Engine — Main App
 * Assembles all tabs and manages global state
 */
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ADS_BUILTIN } from './data/adsItems.js';
import { DEFAULT_LOADS } from './data/defaultLoads.js';
import { LABOR_HOURS } from './data/laborHours.js';
import { MKT, FREIGHT_ROUTES, FREIGHT_RATES } from './data/marketPricing.js';
import { calcBucketCost, calcInfraCost, nemaFromHp } from './utils/quoteEngine.js';
import { parseItemMaster } from './utils/itemMasterParser.js';
import { interpretDrawing } from './utils/claudeDrawingParser.js';
import { isAuthed, fetchMe, clearToken } from './utils/auth.js';
import { fetchLaborEstimate, buildPanelBom, importDrawingToPaneliq } from './utils/laborClient.js';

import LoginPage     from './components/LoginPage.jsx';
import DrawingTab    from './components/tabs/DrawingTab.jsx';
import ItemMasterTab from './components/tabs/ItemMasterTab.jsx';
import LoadsTab      from './components/tabs/LoadsTab.jsx';
import SummaryTab    from './components/tabs/SummaryTab.jsx';
import BucketsTab    from './components/tabs/BucketsTab.jsx';
import InfraTab      from './components/tabs/InfraTab.jsx';
import LaborTab      from './components/tabs/LaborTab.jsx';
import FreightTab    from './components/tabs/FreightTab.jsx';
import CompareTab    from './components/tabs/CompareTab.jsx';

const fmt    = n => '$' + Math.round(n).toLocaleString();
const fmtHrs = n => Math.round(n) + 'h';
const pct    = n => Math.round(n) + '%';

const C = {
  ads:    '#7c3aed',
  labor:  '#0369a1',
  oh:     '#b45309',
  freight:'#0f766e',
  ok:     '#16a34a',
  mkt:    ['#22c55e', '#f59e0b', '#ef4444'],
};

function MccQuoteApp({ onLogout, user }) {
  // ── Drawing ──
  // drawingFile: the raw File from <input type=file>, sent to the proxy as
  //   multipart so the server can branch on PDF vs image.
  // drawingImg:  base64 (no data: prefix) for in-page <img> preview. Null for PDFs.
  const [drawingFile, setDrawingFile] = useState(null);
  const [drawingImg,  setDrawingImg]  = useState(null);
  const [drawingName, setDrawingName] = useState('');
  const [aiStatus,    setAiStatus]    = useState('idle');
  const [aiLog,       setAiLog]       = useState('');
  // ── ItemMaster ──
  const [imStatus,  setImStatus]  = useState('idle');
  const [imCount,   setImCount]   = useState(0);
  const [customADS, setCustomADS] = useState({});
  const ADS = Object.keys(customADS).length > 0 ? customADS : ADS_BUILTIN;
  // ── Loads ──
  const [loads,       setLoads]       = useState(DEFAULT_LOADS);
  const [editingLoad, setEditingLoad] = useState(null);
  // ── Quote config ──
  const [st,          setSt]          = useState('soft');
  const [encl,        setEncl]        = useState('indoor12');
  const [ctrl,        setCtrl]        = useState('120');
  const [tier,        setTier]        = useState(1);
  const [margin,      setMargin]      = useState(35);
  const [laborRate,   setLabor]       = useState(85);
  const [ohPct,       setOh]          = useState(20);
  const [tab,         setTab]         = useState('drawing');
  const [route,       setRoute]       = useState(Object.keys(FREIGHT_ROUTES)[0]);
  const [custMiles,   setCustMiles]   = useState(1480);
  const [freightMode, setFreightMode] = useState('ftl_flatbed');

  const NS = useMemo(() => Math.max(6, Math.ceil(loads.length / 3) + 2), [loads]);

  // ── Save to paneliq (creates Project + Design + Revision + BOM lines) ──
  const [saveProjectName, setSaveProjectName] = useState('');
  const [saveDesignName, setSaveDesignName] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle');  // idle | saving | ok | error
  const [saveResult, setSaveResult] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [lastDrawingMeta, setLastDrawingMeta] = useState(null);

  // ── Backend labor estimate ──
  // The backend's labor_service.estimate_labor is the canonical labor model
  // for paneliq. We send a BOM derived from the current loads + infra and
  // overlay its per-component hours back onto buckets/infra for display.
  const [laborEstimate, setLaborEstimate] = useState(null);
  const [laborSource, setLaborSource] = useState('local');  // 'local' | 'backend' | 'error'

  const hoursByTag = useMemo(() => {
    if (!laborEstimate || !laborEstimate.component_lines) return null;
    const map = {};
    laborEstimate.component_lines.forEach((cl) => {
      const t = cl.tag || '';
      if (!t) return;
      map[t] = (map[t] || 0) + (cl.total_hours || 0);
    });
    return map;
  }, [laborEstimate]);

  // ── Drawing upload ──
  const drawingRef = useRef();
  const handleDrawingUpload = useCallback(e => {
    const file = e.target.files[0];
    if (!file) return;
    setDrawingFile(file);
    setDrawingName(file.name);
    setAiStatus('idle');
    setAiLog('');
    // Build a preview for image files only; PDFs just show the filename.
    if (file.type && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = ev => {
        setDrawingImg(ev.target.result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    } else {
      setDrawingImg(null);
    }
  }, []);

  // ── Claude API call (via paneliq /vision/interpret-drawing proxy) ──
  const runDrawingAI = useCallback(async () => {
    if (!drawingFile) return alert('Upload a drawing first.');
    setAiStatus('loading');
    setAiLog('Sending drawing to Claude AI…');
    try {
      const result = await interpretDrawing(drawingFile);
      setLoads(result.loads);
      setLastDrawingMeta(result.meta || null);
      // Pre-fill the project name from the extracted projectName when blank.
      if (result.meta?.projectName && !saveProjectName) {
        setSaveProjectName(result.meta.projectName);
      }
      setAiStatus('done');
      const m = result.meta;
      const pi = m.pageInfo;
      const pageLine = pi
        ? `Pages analyzed: ${(pi.selected_pages || []).join(', ')} of ${pi.total_pages}` +
          (pi.skipped_pages ? ` (${pi.skipped_pages} text-only pages skipped)` : '') +
          '\n'
        : '';
      setAiLog(
        `Extracted ${result.loads.length} loads\n` +
        pageLine +
        `Project: ${m.projectName || 'Unknown'} | ${m.voltage || ''}\n` +
        `Main Breaker: ${m.mainBreakerAmps || '?'}A | ~${m.estimatedSections || NS} sections\n` +
        `Confidence: ${m.confidence || '?'}\n` +
        (m.extractionNotes ? `Notes: ${m.extractionNotes}` : '')
      );
      setTab('loads');
    } catch (err) {
      setAiStatus('error');
      setAiLog('Error: ' + err.message);
      // If the session expired, drop back to the login page.
      if (/sign in/i.test(err.message || '')) onLogout?.();
    }
  }, [drawingFile, NS, onLogout]);

  const handleSaveToPaneliq = useCallback(async () => {
    if (!saveProjectName.trim()) {
      setSaveError('Project name is required.');
      setSaveStatus('error');
      return;
    }
    if (loads.length === 0) {
      setSaveError('Upload and analyze a drawing first.');
      setSaveStatus('error');
      return;
    }
    setSaveStatus('saving');
    setSaveError('');
    try {
      const payload = {
        projectName: saveProjectName,
        designName: saveDesignName || null,
        starterType: st,
        loads,
        meta: lastDrawingMeta,
      };
      const res = await importDrawingToPaneliq(payload);
      setSaveResult(res);
      setSaveStatus('ok');
    } catch (err) {
      setSaveError(err.message || 'Save failed');
      setSaveStatus('error');
    }
  }, [saveProjectName, saveDesignName, st, loads, lastDrawingMeta]);

  // Fire a labor-estimate fetch whenever the BOM-shaping inputs change.
  // We compute the BOM inside the effect to avoid loops over derived state.
  useEffect(() => {
    if (!loads || loads.length === 0) {
      setLaborEstimate(null);
      setLaborSource('local');
      return undefined;
    }
    // calcInfraCost is pure; recompute the *base* (no backend overlay) BOM.
    const baseInfra = calcInfraCost(encl, ctrl, ADS, NS);
    const bom = buildPanelBom(loads, st, baseInfra);
    let cancelled = false;
    fetchLaborEstimate(bom).then(
      (result) => {
        if (cancelled) return;
        setLaborEstimate(result);
        setLaborSource('backend');
      },
      (err) => {
        if (cancelled) return;
        console.warn('[laborEstimate] falling back to local LABOR_HOURS:', err.message);
        setLaborEstimate(null);
        setLaborSource('error');
      }
    );
    return () => { cancelled = true; };
  }, [loads, st, encl, ctrl, ADS, NS]);

  // ── ItemMaster import ──
  const imRef = useRef();
  const handleItemMaster = useCallback(e => {
    const file = e.target.files[0];
    if (!file) return;
    setImStatus('loading');
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const { updatedADS, totalItems, matchedKeys } = parseItemMaster(ev.target.result);
        setCustomADS(updatedADS);
        setImCount(totalItems);
        setImStatus('done');
        alert(`ItemMaster loaded: ${totalItems.toLocaleString()} items\n${matchedKeys} ADS pricing keys matched`);
      } catch (err) {
        setImStatus('error');
        alert('Parse error: ' + err.message);
      }
    };
    reader.readAsText(file);
  }, []);

  // ── Load editor ──
  const addLoad = () => {
    const id = 'load_' + Date.now();
    setLoads(l => [...l, { id, tag: 'NEW-LOAD', hp: 15, nemaSize: 3, description: '' }]);
    setEditingLoad(id);
  };
  const removeLoad = id => setLoads(l => l.filter(x => x.id !== id));
  const updateLoad = (id, field, val) => setLoads(l => l.map(x =>
    x.id === id ? {
      ...x,
      [field]: field === 'hp' ? Number(val) : val,
      nemaSize: field === 'hp' ? nemaFromHp(Number(val)) : x.nemaSize,
    } : x
  ));

  // ── Quote calculations ──
  const buckets = useMemo(() => loads.map(l => {
    const tbl  = MKT[st];
    const ns   = l.nemaSize;
    const mktP = (!l.hp || l.hp === 0) ? tbl.ctrl[tier] : (tbl[ns <= 0 ? 0 : ns] || tbl[0])[tier];
    const { mat, hrs: localHrs, note } = calcBucketCost(st, l, ADS);
    // Prefer backend-supplied hours when the labor estimate is available.
    const backendHrs = hoursByTag ? hoursByTag[l.tag] : undefined;
    const hrs = backendHrs != null ? backendHrs : localHrs;
    const laborCost = hrs * laborRate * (1 + ohPct / 100);
    const totalCost = mat + laborCost;
    const sell      = Math.round(totalCost / (1 - margin / 100));
    return { ...l, mktP, mat, hrs, laborCost: Math.round(laborCost), totalCost: Math.round(totalCost), sell, gp: sell - Math.round(totalCost), note };
  }), [loads, st, tier, margin, laborRate, ohPct, ADS, hoursByTag]);

  const totalBktMkt   = buckets.reduce((s, b) => s + b.mktP, 0);
  const totalBktMat   = buckets.reduce((s, b) => s + b.mat, 0);
  const totalBktHrs   = buckets.reduce((s, b) => s + b.hrs, 0);
  const totalBktLabor = buckets.reduce((s, b) => s + b.laborCost, 0);
  const totalBktCost  = buckets.reduce((s, b) => s + b.totalCost, 0);
  const totalBktSell  = buckets.reduce((s, b) => s + b.sell, 0);

  const infraRows = useMemo(() => {
    const base = calcInfraCost(encl, ctrl, ADS, NS);
    if (!hoursByTag) return base;
    return base.map((row, i) => {
      const backendHrs = hoursByTag[`infra:${i}`];
      return backendHrs != null ? { ...row, hrs: backendHrs } : row;
    });
  }, [encl, ctrl, ADS, NS, hoursByTag]);
  const totalInfraMat   = infraRows.reduce((s, r) => s + r.mat, 0);
  const totalInfraHrs   = infraRows.reduce((s, r) => s + r.hrs, 0);
  const totalInfraLabor = Math.round(totalInfraHrs * laborRate * (1 + ohPct / 100));
  const totalInfraCost  = Math.round(totalInfraMat + totalInfraLabor);
  const totalInfraSell  = Math.round(totalInfraCost / (1 - margin / 100));
  const totalInfraMkt   = MKT.structure[encl][tier] * NS + MKT.bus[tier] * NS
    + MKT.mainBreaker[tier] + MKT.spd[tier] + MKT.meter[tier]
    + MKT.xfmr[tier] + MKT.pdp[tier] + MKT.enip[tier] * 2
    + MKT.cpt[tier] * NS + (ctrl !== '120' ? MKT.psu24[tier] * 2 : 0);

  const qaHrs       = Math.round((totalBktHrs + totalInfraHrs) * LABOR_HOURS.shop_qa_pct);
  const shipHrs     = LABOR_HOURS.shop_ship_hrs;
  const qaShipLabor = Math.round((qaHrs + shipHrs) * laborRate * (1 + ohPct / 100));
  const grandHrs    = totalBktHrs + totalInfraHrs + qaHrs + shipHrs;
  const grandLabor  = totalBktLabor + totalInfraLabor + qaShipLabor;
  const grandMat    = totalBktMat + totalInfraMat;
  const grandCost   = grandMat + grandLabor;

  const miles         = route === 'Custom Route' ? custMiles : FREIGHT_ROUTES[route].miles;
  const ratePerMile   = FREIGHT_RATES[freightMode] || 2.94;
  const baseFrt       = Math.round(miles * ratePerMile);
  const totalFreight  = baseFrt + Math.round(baseFrt * 0.18) + 250 + (freightMode === 'ltl' ? 350 : 0);

  const grandSellNoFrt = Math.round(grandCost / (1 - margin / 100));
  const grandGP        = grandSellNoFrt - grandCost;
  const gpPctGrand     = grandSellNoFrt > 0 ? Math.round((grandGP / grandSellNoFrt) * 100) : 0;
  const totalMktWithEF = (totalBktMkt + totalInfraMkt) * 1.12;

  // ── Render helpers ──
  const tabBtn = v => ({
    padding: '7px 13px', border: 'none', cursor: 'pointer', borderRadius: 6, fontSize: 12,
    background: tab === v ? '#1e3a5f' : '#e2e8f0',
    color: tab === v ? '#fff' : '#374151',
    fontWeight: tab === v ? 700 : 400,
  });

  const btn = (label, val, cur, set, color = '#2563eb') => (
    <button key={val} onClick={() => set(val)} style={{
      padding: '6px 11px', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 12,
      background: cur === val ? color : '#e2e8f0',
      color: cur === val ? '#fff' : '#374151',
      fontWeight: cur === val ? 700 : 400,
    }}>{label}</button>
  );

  const sliderCtrl = (label, val, set, min, max, step, color, pre = '', suf = '') => (
    <div style={{ minWidth: 145 }}>
      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 3, color: '#374151' }}>
        {label}: <span style={{ color }}>{pre}{val.toLocaleString()}{suf}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={val}
        onChange={e => set(+e.target.value)} style={{ width: '100%', accentColor: color }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8' }}>
        <span>{pre}{min}{suf}</span><span>{pre}{max}{suf}</span>
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily: 'system-ui,sans-serif', fontSize: 13, background: '#f1f5f9', minHeight: '100vh', padding: 12 }}>
      <div style={{ maxWidth: 1350, margin: '0 auto' }}>
        {/* HEADER */}
        <div style={{ background: '#1e3a5f', color: '#fff', borderRadius: 10, padding: '12px 20px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>MCC Quote Engine</div>
            <div style={{ fontSize: 11, opacity: .7, marginTop: 2 }}>AB CENTERLINE 2100 | 480V 3-Phase 65kAIC EtherNet/IP | AI Drawing + ItemMaster</div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              ['ADS LPP', C.ads],
              ['Labor Algo', C.labor],
              ['Freight', C.freight],
              [imStatus === 'done' ? `IM (${imCount.toLocaleString()})` : 'No IM', imStatus === 'done' ? C.ok : '#92400e'],
              [aiStatus === 'done' ? 'AI Drawing' : 'AI Ready', aiStatus === 'done' ? C.ok : '#374151'],
              [
                laborSource === 'backend' ? 'Labor: paneliq API'
                : laborSource === 'error' ? 'Labor: local (API err)'
                : 'Labor: local',
                laborSource === 'backend' ? C.ok : laborSource === 'error' ? '#b91c1c' : '#92400e',
              ],
            ].map(([t, c]) => (
              <div key={t} style={{ background: c, borderRadius: 5, padding: '4px 10px', fontSize: 11, fontWeight: 700 }}>{t}</div>
            ))}
            {user?.email && (
              <div style={{ fontSize: 11, opacity: .75, marginLeft: 6 }}>{user.email}</div>
            )}
            <button
              type="button"
              onClick={onLogout}
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '4px 10px',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 5,
                background: 'transparent',
                color: '#fff',
                cursor: 'pointer',
                marginLeft: 6,
              }}
            >
              Log out
            </button>
          </div>
        </div>

        {/* CONTROLS */}
        <div style={{ background: '#fff', borderRadius: 10, padding: 12, marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Starter</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {btn('Soft Start', 'soft', st, setSt, C.ads)}
              {btn('VFD', 'vfd', st, setSt, C.labor)}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Enclosure</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {btn('Indoor N12', 'indoor12', encl, setEncl)}
              {btn('Outdoor N4X SS', 'outdoor4X', encl, setEncl)}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Control V</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {[['120V', '120'], ['24V', '24'], ['Both', 'both']].map(([l, v]) => btn(l, v, ctrl, setCtrl))}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Mkt Tier</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {['Low', 'Mid', 'High'].map((l, i) => btn(l, i, tier, setTier, C.mkt[i]))}
            </div>
          </div>
          {sliderCtrl('Margin', margin, setMargin, 15, 60, 1, C.ads, '', '%')}
          {sliderCtrl('Labor', laborRate, setLabor, 30, 250, 5, C.labor, '$', '/hr')}
          {sliderCtrl('OH', ohPct, setOh, 10, 35, 1, C.oh, '', '%')}
        </div>

        {/* SAVE TO PANELIQ — appears once loads are loaded */}
        {loads.length > 0 && (
          <div style={{
            background: '#fff', borderRadius: 10, padding: 12, marginBottom: 12,
            display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
            border: saveStatus === 'ok' ? `2px solid ${C.ok || '#16a34a'}` : '1px solid #e2e8f0',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginRight: 4 }}>
              Save to paneliq:
            </div>
            <input
              type="text"
              value={saveProjectName}
              onChange={(e) => setSaveProjectName(e.target.value)}
              placeholder="Project name (required)"
              disabled={saveStatus === 'saving'}
              style={{
                fontSize: 12, padding: '6px 10px',
                border: '1px solid #cbd5e1', borderRadius: 5,
                minWidth: 200,
              }}
            />
            <input
              type="text"
              value={saveDesignName}
              onChange={(e) => setSaveDesignName(e.target.value)}
              placeholder="Design name (optional)"
              disabled={saveStatus === 'saving'}
              style={{
                fontSize: 12, padding: '6px 10px',
                border: '1px solid #cbd5e1', borderRadius: 5,
                minWidth: 220,
              }}
            />
            <button
              type="button"
              onClick={handleSaveToPaneliq}
              disabled={saveStatus === 'saving' || !saveProjectName.trim()}
              style={{
                fontSize: 12, fontWeight: 700, padding: '6px 16px',
                border: 'none', borderRadius: 5, color: '#fff',
                background: (saveStatus === 'saving' || !saveProjectName.trim())
                  ? '#94a3b8' : '#1e3a5f',
                cursor: (saveStatus === 'saving' || !saveProjectName.trim())
                  ? 'not-allowed' : 'pointer',
              }}
            >
              {saveStatus === 'saving' ? 'Saving…' : 'Save to paneliq'}
            </button>
            <div style={{ fontSize: 11, color: '#64748b', marginLeft: 'auto' }}>
              Creates Project / Design / Revision + {loads.length} BOM line{loads.length !== 1 ? 's' : ''} in paneliq.
            </div>

            {saveStatus === 'ok' && saveResult && (
              <div style={{
                width: '100%', marginTop: 8, padding: 10, borderRadius: 6,
                background: '#dcfce7', color: '#166534', fontSize: 12,
              }}>
                <strong>Saved.</strong> &nbsp;
                Project <code>{saveResult.project_id.slice(0, 8)}</code>
                {saveResult.project_created ? ' (new)' : ' (existing)'},
                Design <code>{saveResult.design_id.slice(0, 8)}</code>
                {saveResult.design_created ? ' (new)' : ' (existing)'},
                Revision <strong>rev {saveResult.rev}</strong>,
                {saveResult.bom_lines_inserted} BOM lines inserted.
                <span style={{ marginLeft: 8 }}>
                  Open it in the Panel Builder (Streamlit, port 8501) to push it through gates / pricing.
                </span>
              </div>
            )}
            {saveStatus === 'error' && saveError && (
              <div style={{
                width: '100%', marginTop: 8, padding: 10, borderRadius: 6,
                background: '#fee2e2', color: '#991b1b', fontSize: 12,
              }}>
                Save failed: {saveError}
              </div>
            )}
          </div>
        )}

        {/* TABS */}
        <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
          {[
            ['drawing', 'Drawing AI'],
            ['itemmaster', 'ItemMaster'],
            ['loads', `Loads (${loads.length})`],
            ['summary', 'Summary'],
            ['buckets', 'Buckets'],
            ['infra', 'Infra'],
            ['labor', 'Labor'],
            ['freight', 'Freight'],
            ['compare', 'Compare'],
          ].map(([v, l]) => <button key={v} style={tabBtn(v)} onClick={() => setTab(v)}>{l}</button>)}
        </div>

        {/* TAB CONTENT */}
        {tab === 'drawing' && <DrawingTab {...{ drawingFile, drawingImg, drawingName, drawingRef, handleDrawingUpload, runDrawingAI, aiStatus, aiLog }} />}
        {tab === 'itemmaster' && <ItemMasterTab {...{ imRef, handleItemMaster, imStatus, imCount, ADS }} />}
        {tab === 'loads' && <LoadsTab {...{ loads, editingLoad, setEditingLoad, addLoad, removeLoad, updateLoad, setLoads, NS, aiStatus, DEFAULT_LOADS }} />}
        {tab === 'summary' && <SummaryTab {...{ grandMat, grandLabor, qaHrs, shipHrs, qaShipLabor, grandHrs, laborRate, ohPct, totalFreight, miles, freightMode, grandCost, grandSellNoFrt, grandGP, gpPctGrand, totalMktWithEF, totalBktMat, totalInfraMat, totalBktLabor, totalInfraLabor, C, fmt, pct }} />}
        {tab === 'buckets' && <BucketsTab {...{ buckets, totalBktMkt, totalBktMat, totalBktHrs, totalBktLabor, totalBktCost, totalBktSell, st, tier, margin, C, fmt, fmtHrs, pct }} />}
        {tab === 'infra' && <InfraTab {...{ infraRows, totalInfraMat, totalInfraHrs, totalInfraLabor, totalInfraCost, totalInfraSell, laborRate, ohPct, margin, NS, C, fmt, fmtHrs, pct }} />}
        {tab === 'labor' && <LaborTab {...{ totalBktHrs, totalInfraHrs, qaHrs, shipHrs, grandHrs, grandLabor, grandMat, laborRate, ohPct, margin, C, fmt, fmtHrs, pct }} />}
        {tab === 'freight' && <FreightTab {...{ route, setRoute, custMiles, setCustMiles, freightMode, setFreightMode, miles, ratePerMile, baseFrt, totalFreight, NS, C, fmt, btn, FREIGHT_ROUTES }} />}
        {tab === 'compare' && <CompareTab {...{ loads, encl, ctrl, NS, totalInfraMat, totalInfraHrs, totalInfraMkt, qaHrs, shipHrs, laborRate, ohPct, margin, tier, C, fmt, pct, ADS, MKT }} />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Top-level App: handles auth state and renders LoginPage or the quote engine.
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  // 'unknown' until we've validated any stored token; then 'in' or 'out'.
  const [authState, setAuthState] = useState(isAuthed() ? 'checking' : 'out');
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (authState !== 'checking') return;
    let cancelled = false;
    (async () => {
      const me = await fetchMe();
      if (cancelled) return;
      if (me) {
        setUser(me);
        setAuthState('in');
      } else {
        clearToken();
        setAuthState('out');
      }
    })();
    return () => { cancelled = true; };
  }, [authState]);

  const handleLogin = (loginPayload) => {
    setUser(loginPayload.user || null);
    setAuthState('in');
  };

  const handleLogout = () => {
    clearToken();
    setUser(null);
    setAuthState('out');
  };

  if (authState === 'checking') {
    return (
      <div style={{
        minHeight: '100vh', background: '#0f172a', color: '#cbd5e1',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'system-ui, sans-serif', fontSize: 14,
      }}>
        Loading…
      </div>
    );
  }

  if (authState === 'out') {
    return <LoginPage onLogin={handleLogin} />;
  }

  return <MccQuoteApp onLogout={handleLogout} user={user} />;
}
